import { useEffect, useState } from 'react'

import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, ColorScheme, Flex, Text } from '@hivewatch/dark'

import { ControlledTextInput, DarkAddressSearch } from '@/components/ui'

import { ShowAddress } from '../components/ShowAddress'
import { ShowCoordinates } from '../components/ShowCoordinates'
import { StaticMap } from '../components/StaticMap'
import { FormInputsIProps } from '../types/types'
import { validateLatitude, validateLongitude } from '../utils/validation'

interface DarkMarketIncidentFormIProps {
  form: UseFormMethods<FormInputsIProps>
  colorScheme: ColorScheme
}

/**
 * Rebrand variant of {@link MarketIncidentForm}. The address ⇄ coordinates
 * toggle, the `address` register/unregister dance and both validators are
 * unchanged; latitude/longitude move from uncontrolled `register` inputs to
 * the controlled dark TextInput, which is the only way to bind them.
 */
export const DarkMarketIncidentForm = ({
  form,
  colorScheme,
}: DarkMarketIncidentFormIProps) => {
  const { t } = useTranslation()
  const [shouldShowAddress, setShowAddress] = useState(true)
  const {
    register,
    unregister,
    setValue,
    trigger,
    watch,
    control,
    errors,
    formState: { isSubmitting },
  } = form

  useEffect(() => {
    register('address', { required: true })
  }, [])

  const toggleShowAddress = () => {
    if (shouldShowAddress) {
      setValue('address', null)
      unregister('address')
    } else {
      setValue('latitude', null)
      setValue('longitude', null)
      register('address', { required: true })
    }
    setShowAddress((prevShowAddress) => !prevShowAddress)
  }

  const watchCoordinates = watch(['latitude', 'longitude'])
  const watchAddress = watch('address')

  const shouldShowAddressMap = watchAddress?.value
  const shouldShowCoordinatesMap =
    watchCoordinates?.latitude &&
    watchCoordinates?.longitude &&
    !errors.longitude &&
    !errors.latitude

  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  return (
    <Flex direction='column' gap='16px'>
      {shouldShowAddress ? (
        <>
          <Box>
            <Text color={labelColor} mb='4px' textStyle='label'>
              {t('common:address')}
            </Text>
            <DarkAddressSearch
              colorScheme={colorScheme}
              isSubmitting={isSubmitting}
              onSelectAddress={(address) => {
                setValue('address', address)
                trigger(['address'])
              }}
            />
          </Box>
          {shouldShowAddressMap && (
            <StaticMap
              latitude={watchAddress?.latitude}
              longitude={watchAddress?.longitude}
            />
          )}
          <ShowCoordinates onClick={toggleShowAddress} />
        </>
      ) : (
        <>
          <Flex gap='12px'>
            <Box flex='1'>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                errors={errors}
                label={t('incidents:incident_create_latitude_label')}
                name='latitude'
                placeholder={t('incidents:incident_create_latitude_label')}
                rules={{ validate: validateLatitude }}
                width='100%'
              />
            </Box>
            <Box flex='1'>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                errors={errors}
                label={t('incidents:incident_create_longitude_label')}
                name='longitude'
                placeholder={t('incidents:incident_create_longitude_label')}
                rules={{ validate: validateLongitude }}
                width='100%'
              />
            </Box>
          </Flex>
          {shouldShowCoordinatesMap && (
            <StaticMap
              latitude={watchCoordinates?.latitude}
              longitude={watchCoordinates?.longitude}
            />
          )}
          <ShowAddress onClick={toggleShowAddress} />
        </>
      )}
    </Flex>
  )
}
